// --------------------------------------------------------------
// Created On: 2022-09-14
// Author: Zachary Thomas
//
// Last Modified: 2025-04-25
// Modified By: Zachary Thomas
//
// Copyright 2024 - 2025 © Cornell Pump Company, All Rights Reserved
// --------------------------------------------------------------

import React, { Fragment, useMemo } from "react";
import PropTypes from "prop-types";
import styles from "./DataHistoryControls.module.scss";

// Controls for changing data history settings.
export default function DataHistoryControls(props: Props): Component {
  const columnSizeStyle = useMemo(
    () => getColumnSizeSettings(props.allowModeControls, props.allowAxisControls),
    [props.allowModeControls, props.allowAxisControls]
  );

  // Gets the column size settings for specific controls based on which controls are enabled.
  function getColumnSizeSettings(allowModeControls: boolean, allowAxisControls: boolean): string {
    if (allowModeControls && allowAxisControls) {
      // Both of the optional controls are allowed.
      return "col-6 col-md-3";
    } else if (allowModeControls !== allowAxisControls) {
      // One of the two optional controls are allowed.
      return "col-6 col-md-4";
    } else {
      // Neither of the optional controls are allowed.
      return "col-6";
    }
  }

  // Stores axis settings to local storage and bubble up the axisMode value to the parent component.
  function handleChangeAxisMode(axisMode: AxisMode): void {
    localStorage.setItem("axisMode", axisMode);
    props.onChangeAxisMode(axisMode);
  }

  return (
    <div className={`${styles.body} row mx-1`}>
      <div className={columnSizeStyle}>
        <label>Start Date</label>
        <input
          className="form-control"
          type="date"
          value={props.startDate}
          onChange={(e) => props.onChangeStartDate(e.target.value)}
        />
      </div>

      <div className={columnSizeStyle}>
        <label>End Date</label>
        <input
          className="form-control"
          type="date"
          value={props.endDate}
          onChange={(e) => props.onChangeEndDate(e.target.value)}
        />
      </div>

      {props.allowModeControls && (
        <div className={columnSizeStyle}>
          <label>Mode</label>
          <select
            className="form-select"
            value={props.graphingMode}
            onChange={(e) => props.onChangeGraphingMode(e.target.value as GraphingMode)}
          >
            <option value="STANDARD">Standard Line Chart</option>
            <option value="MOVING_AVERAGE">Moving Average Line Chart</option>
            {props.allowAccumulation && (
              <Fragment>
                <option value="HOURLY_ACCUMULATION">Hourly Accumulation</option>
                <option value="DAILY_ACCUMULATION">Daily Accumulation</option>
                <option value="WEEKLY_ACCUMULATION">Weekly Accumulation</option>
                <option value="MONTHLY_ACCUMULATION">Monthly Accumulation</option>
              </Fragment>
            )}
          </select>
        </div>
      )}

      {props.allowAxisControls && (
        <div className={columnSizeStyle}>
          <label>Axis Labels</label>
          <select
            className="form-select"
            value={props.axisMode}
            onChange={(e) => handleChangeAxisMode(e.target.value as AxisMode)}
          >
            <option value="Y_AXIS_PER_UNIT">Y-axis Per Unit Of Measure</option>
            <option value="Y_AXIS_PER_ATTRIBUTE">Y-axis Per Attribute</option>
          </select>
        </div>
      )}
    </div>
  );
}

DataHistoryControls.propTypes = {
  startDate: PropTypes.string.isRequired,
  endDate: PropTypes.string.isRequired,
  graphingMode: PropTypes.string.isRequired,
  axisMode: PropTypes.string.isRequired,
  allowAccumulation: PropTypes.bool.isRequired,
  allowModeControls: PropTypes.bool.isRequired,
  allowAxisControls: PropTypes.bool.isRequired,
  onChangeStartDate: PropTypes.func.isRequired,
  onChangeEndDate: PropTypes.func.isRequired,
  onChangeGraphingMode: PropTypes.func.isRequired,
  onChangeAxisMode: PropTypes.func.isRequired,
};

interface Props {
  startDate: string;
  endDate: string;
  graphingMode: GraphingMode;
  axisMode: AxisMode;
  allowAccumulation: boolean;
  allowModeControls: boolean;
  allowAxisControls: boolean;
  onChangeStartDate: (startDate: string) => void;
  onChangeEndDate: (endDate: string) => void;
  onChangeGraphingMode: (mode: GraphingMode) => void;
  onChangeAxisMode: (mode: AxisMode) => void;
}

type GraphingMode =
  | "STANDARD"
  | "MOVING_AVERAGE"
  | "HOURLY_ACCUMULATION"
  | "DAILY_ACCUMULATION"
  | "WEEKLY_ACCUMULATION"
  | "MONTHLY_ACCUMULATION";

type AxisMode = "Y_AXIS_PER_ATTRIBUTE" | "Y_AXIS_PER_UNIT";
